import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Home, ClipboardCheck, Target, Layers, Youtube, BarChart3, MessageCircle, DownloadCloud, Lock,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { DownloadsPanel } from '@/components/sidebar/DownloadsPanel';
import { AreaSwitcher } from '@/components/oposiciones/AreaSwitcher';
import { hasFullAppAccess } from '@/lib/authRedirect';
import { ALL_OPOS_AREA_IDS, canSwitchAllOposiciones, isBasicOposicionesModuleAllowed, isOposicionesBasicPlan } from '@/lib/oposicionesAccess';
import { QuotaUpgradeModal } from './QuotaUpgradeModal';

export type CuerpoAccent = 'green' | 'red' | 'blue' | 'orange' | 'sky' | 'amber';

interface CuerpoNavbarProps {
  basePath: string;
  shortLabel: string;
  badgeImage?: string;
  accent: CuerpoAccent;
  areaId: string;
  downloadsMode: any;
  caseLabel?: string;
  hideCase?: boolean;
  extraNavItems?: { id: string; label: string; icon: LucideIcon; path: string }[];
}

const ACCENT_MAP: Record<CuerpoAccent, { active: string; border: string; hoverBg: string; iconColor: string; bgPanel: string; textPanel: string; switcherAccent: 'violet' | 'blue' | 'neutral' }> = {
  green:  { active: 'bg-green-100 text-green-800 hover:bg-green-100',  border: 'border-green-200',  hoverBg: 'hover:bg-green-50',  iconColor: 'text-green-700',  bgPanel: 'bg-green-50/50',  textPanel: 'text-green-900',  switcherAccent: 'neutral' },
  red:    { active: 'bg-red-100 text-red-800 hover:bg-red-100',         border: 'border-red-200',    hoverBg: 'hover:bg-red-50',    iconColor: 'text-red-700',    bgPanel: 'bg-red-50/50',    textPanel: 'text-red-900',    switcherAccent: 'neutral' },
  blue:   { active: 'bg-blue-100 text-blue-800 hover:bg-blue-100',      border: 'border-blue-200',   hoverBg: 'hover:bg-blue-50',   iconColor: 'text-blue-700',   bgPanel: 'bg-blue-50/50',   textPanel: 'text-blue-900',   switcherAccent: 'blue' },
  orange: { active: 'bg-orange-100 text-orange-800 hover:bg-orange-100',border: 'border-orange-200', hoverBg: 'hover:bg-orange-50', iconColor: 'text-orange-700', bgPanel: 'bg-orange-50/50', textPanel: 'text-orange-900', switcherAccent: 'neutral' },
  sky:    { active: 'bg-sky-100 text-sky-800 hover:bg-sky-100',         border: 'border-sky-200',    hoverBg: 'hover:bg-sky-50',    iconColor: 'text-sky-700',    bgPanel: 'bg-sky-50/50',    textPanel: 'text-sky-900',    switcherAccent: 'blue' },
  amber:  { active: 'bg-amber-100 text-amber-800 hover:bg-amber-100',   border: 'border-amber-200',  hoverBg: 'hover:bg-amber-50',  iconColor: 'text-amber-700',  bgPanel: 'bg-amber-50/50',  textPanel: 'text-amber-900',  switcherAccent: 'neutral' },
};

export function CuerpoNavbar({ basePath, shortLabel, badgeImage, accent, areaId, downloadsMode, caseLabel = 'Supuestos', hideCase = false, extraNavItems = [] }: CuerpoNavbarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { profile } = useAuth();
  const a = ACCENT_MAP[accent];
  const isBasic = isOposicionesBasicPlan(profile?.plan);
  const [lockedLabel, setLockedLabel] = useState<string | null>(null);

  type Item = { id: string; label: string; icon: LucideIcon; path: string; isExtra: boolean };
  const baseNav: Item[] = [
    { id: 'home',       label: 'Inicio',     icon: Home,           path: basePath, isExtra: false },
    { id: 'test',       label: 'Tests',      icon: ClipboardCheck, path: `${basePath}/test`, isExtra: false },
    ...(hideCase ? [] : [{ id: 'caso', label: caseLabel, icon: Target, path: `${basePath}/caso`, isExtra: false } as Item]),
    { id: 'flashcards', label: 'Flashcards', icon: Layers,         path: `${basePath}/flashcards`, isExtra: false },
    ...extraNavItems.map((i) => ({ ...i, isExtra: true })),
    { id: 'recursos',   label: 'Recursos',   icon: Youtube,        path: `${basePath}/recursos`, isExtra: false },
    { id: 'progreso',   label: 'Progreso',   icon: BarChart3,      path: `${basePath}/progreso`, isExtra: false },
    { id: 'chat',       label: 'Chat',       icon: MessageCircle,  path: `${basePath}/chat`, isExtra: false },
  ];

  const getActiveItem = () => {
    const path = location.pathname;
    const exact = baseNav.find((i) => i.path === path);
    if (exact) return exact.id;
    for (const it of baseNav) {
      if (path.startsWith(it.path) && it.path !== basePath) return it.id;
    }
    return 'home';
  };
  const visibleNav = baseNav;
  const activeItem = getActiveItem();

  // Si hay items extra (p.ej. Guardia Civil con 3), reservamos el nivel "muestra label" para 2xl
  // para que en pantallas medianas no se desborden y tapen los primeros items.
  const hasExtras = extraNavItems.length > 0;
  const labelBreakpoint = 'hidden xl:inline';
  const extraLabelBreakpoint = 'hidden xl:inline';

  return (
    <TooltipProvider delayDuration={200}>
      <header className="bg-white dark:bg-background border-b px-2 sm:px-3 py-2 w-full flex flex-col lg:flex-row lg:items-center gap-2 lg:gap-3">
        {/* Top row on mobile/tablet: logo + actions. On desktop: just logo block. */}
        <div className="flex items-center justify-between gap-2 lg:justify-start lg:shrink-0">
          {/* Logo MunilexAcademy + Badge cuerpo (stacked on mobile) */}
          <div
            className="flex flex-col lg:flex-row lg:items-center gap-1 lg:gap-2 cursor-pointer shrink-0 bg-[hsl(0,0%,8%)] rounded-md px-2 py-1 ring-1 ring-[hsl(45,80%,55%)]/30"
            onClick={() => navigate(basePath)}
          >
            <img src="/munilex-academy-logo.png" alt="MunilexAcademy" className="h-6 lg:h-7 w-auto object-contain self-center" />
            <div className="flex items-center gap-1.5 lg:ml-0.5 lg:border-l border-[hsl(45,80%,55%)]/40 lg:pl-2 self-center">
              {badgeImage && (
                <img
                  src={badgeImage}
                  alt={`Escudo oficial ${shortLabel}`}
                  className="h-5 w-5 lg:h-7 lg:w-7 object-contain drop-shadow"
                  loading="lazy"
                />
              )}
              <span className="font-bold text-[10px] text-[hsl(45,80%,55%)] uppercase tracking-wider whitespace-nowrap">{shortLabel}</span>
            </div>
          </div>

          {/* Actions visible on mobile/tablet only (right side of top row) */}
          <div className="flex items-center gap-1.5 lg:hidden">
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className={cn('gap-1.5 text-xs h-9 px-2', a.border, a.hoverBg)} title="Mis Descargas">
                  <DownloadCloud className="h-3.5 w-3.5" />
                </Button>
              </PopoverTrigger>
              <PopoverContent side="bottom" align="end" className={cn('w-[340px] p-0 shadow-2xl', a.border)}>
                <div className={cn('p-3 border-b flex items-center gap-2', a.bgPanel)}>
                  <DownloadCloud className={cn('h-4 w-4', a.iconColor)} />
                  <span className={cn('text-sm font-semibold', a.textPanel)}>Mis Descargas</span>
                </div>
                <DownloadsPanel mode={downloadsMode} />
              </PopoverContent>
            </Popover>
            {(hasFullAppAccess(profile) || canSwitchAllOposiciones(profile?.plan)) && (
              <AreaSwitcher
                currentAreaId={areaId}
                accent={a.switcherAccent}
                label={profile?.role === 'super_admin' ? 'Acceso total' : canSwitchAllOposiciones(profile?.plan) ? "Tus Oposiciones" : undefined}
                allowedIds={hasFullAppAccess(profile) ? undefined : canSwitchAllOposiciones(profile?.plan) ? [...ALL_OPOS_AREA_IDS] : undefined}
              />
            )}
          </div>
        </div>

        <nav className="flex-1 min-w-0 w-full lg:w-auto">
          <div className="flex flex-wrap items-center justify-center gap-1 lg:gap-0.5">
            {visibleNav.map((item) => {
              const Icon = item.icon;
              const isActive = activeItem === item.id;
              const isLocked = isBasic && !isBasicOposicionesModuleAllowed(item.id);
              const compactExtra = item.isExtra;
              const btn = (
                <Button
                  variant="ghost"
                  size="sm"
                  className={cn(
                    'gap-1.5 shrink-0 h-9 text-xs sm:text-sm px-2 sm:px-2.5',
                    isActive && !isLocked && a.active,
                    isLocked && 'text-muted-foreground/70'
                  )}
                  onClick={() => isLocked ? setLockedLabel(item.label) : navigate(item.path)}
                  data-tour={item.id === 'home' ? 'home' : `nav-${item.id}`}
                  aria-label={item.label}
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  <span className={cn(compactExtra ? extraLabelBreakpoint : labelBreakpoint)}>
                    {item.label}
                  </span>
                  {isLocked && <Lock className="h-3 w-3" />}
                </Button>
              );
              return (
                <Tooltip key={item.id}>
                  <TooltipTrigger asChild>{btn}</TooltipTrigger>
                  <TooltipContent side="bottom">{item.label}</TooltipContent>
                </Tooltip>
              );
            })}
          </div>
        </nav>

        {/* Desktop-only actions on the right */}
        <div className="hidden lg:flex items-center gap-2">
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className={cn('gap-1.5 text-xs shrink-0 h-9 px-2 sm:px-2.5', a.border, a.hoverBg)} title="Mis Descargas">
                <DownloadCloud className="h-3.5 w-3.5" />
                <span className="hidden xl:inline">Descargas</span>
              </Button>
            </PopoverTrigger>
            <PopoverContent side="bottom" align="end" className={cn('w-[340px] p-0 shadow-2xl', a.border)}>
              <div className={cn('p-3 border-b flex items-center gap-2', a.bgPanel)}>
                <DownloadCloud className={cn('h-4 w-4', a.iconColor)} />
                <span className={cn('text-sm font-semibold', a.textPanel)}>Mis Descargas</span>
              </div>
              <DownloadsPanel mode={downloadsMode} />
            </PopoverContent>
          </Popover>
          {(hasFullAppAccess(profile) || canSwitchAllOposiciones(profile?.plan)) && (
            <AreaSwitcher
              currentAreaId={areaId}
              accent={a.switcherAccent}
              label={profile?.role === 'super_admin' ? 'Acceso total' : canSwitchAllOposiciones(profile?.plan) ? "Tus Oposiciones" : undefined}
              allowedIds={hasFullAppAccess(profile) ? undefined : canSwitchAllOposiciones(profile?.plan) ? [...ALL_OPOS_AREA_IDS] : undefined}
            />
          )}
        </div>
      </header>
      <QuotaUpgradeModal
        open={lockedLabel !== null}
        onOpenChange={(open) => !open && setLockedLabel(null)}
        title={`${lockedLabel || 'Esta función'} requiere Pro`}
        description="Esta función no está incluida en el plan Básico. Mejora a Pro para desbloquearla."
        options={['pro']}
      />
    </TooltipProvider>
  );
}
